import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { getColors, fonts } from '../theme';
import { CALENDAR_COLORS } from '../config/calendarColors';
import { Check, ChevronRight, ChevronLeft, Sparkles, ArrowRight, X, Calendar, Crown, Zap, Plus, Clock, Target, CheckSquare, Flame, TrendingUp, Link2, ListTodo, Repeat, BarChart3, Brain, BookOpen, Wallet, Sprout, Unlock, Eye, Dumbbell, Sunrise, Sun, Moon, Shuffle } from 'lucide-react';

// ============================================================
// EVENT SUGGESTIONS - Rotating animated placeholders
// ============================================================

const EVENT_SUGGESTIONS = [
  { name: "Doctor's appointment" },
  { name: "Coffee with a friend" },
  { name: "Team meeting" },
  { name: "Birthday party" },
  { name: "Gym session" },
  { name: "Date night" },
  { name: "Dentist visit" },
  { name: "Call with Mom" },
  { name: "Project deadline" },
  { name: "Weekend trip" },
];

// ============================================================
// GOAL SUGGESTIONS - Rotating animated placeholders
// ============================================================

const GOAL_SUGGESTIONS = [
  { name: "Run a marathon" },
  { name: "Learn Spanish" },
  { name: "Save $10,000" },
  { name: "Read 24 books this year" },
  { name: "Get promoted at work" },
  { name: "Launch a side project" },
  { name: "Lose 20 pounds" },
  { name: "Travel to 3 new countries" },
  { name: "Learn to play guitar" },
  { name: "Complete a certification" },
];

// ============================================================
// PREFERENCE-BASED HABIT TEMPLATES
// ============================================================

const HABIT_SUGGESTIONS = {
  // Health & Fitness habits
  health: {
    morning: [
      { name: "Morning workout (30 min)", timeOfDay: 'morning', goal: 30 },
      { name: "Morning stretches (10 min)", timeOfDay: 'morning', goal: 30 },
      { name: "Take vitamins", timeOfDay: 'morning', goal: 30 },
    ],
    afternoon: [
      { name: "Take a walk (20 min)", timeOfDay: 'afternoon', goal: 30 },
      { name: "Afternoon workout", timeOfDay: 'afternoon', goal: 30 },
      { name: "Eat a balanced lunch", timeOfDay: 'afternoon', goal: 30 },
    ],
    evening: [
      { name: "Evening run", timeOfDay: 'evening', goal: 30 },
      { name: "Get 7+ hours of sleep", timeOfDay: 'evening', goal: 30 },
      { name: "No screens before bed", timeOfDay: 'evening', goal: 30 },
    ],
    flexible: [
      { name: "Drink 8 glasses of water", timeOfDay: 'anytime', goal: 30 },
      { name: "Exercise (30 min)", timeOfDay: 'anytime', goal: 30 },
      { name: "Eat balanced meals", timeOfDay: 'anytime', goal: 30 },
    ],
  },
  // Productivity habits
  productivity: {
    morning: [
      { name: "Review daily priorities", timeOfDay: 'morning', goal: 30 },
      { name: "Complete MIT (Most Important Task)", timeOfDay: 'morning', goal: 30 },
      { name: "No phone for first hour", timeOfDay: 'morning', goal: 30 },
    ],
    afternoon: [
      { name: "Inbox zero", timeOfDay: 'afternoon', goal: 30 },
      { name: "Deep work session (2 hrs)", timeOfDay: 'afternoon', goal: 30 },
      { name: "Afternoon reset break", timeOfDay: 'afternoon', goal: 30 },
    ],
    evening: [
      { name: "Plan tomorrow's tasks", timeOfDay: 'evening', goal: 30 },
      { name: "Review what got done", timeOfDay: 'evening', goal: 30 },
      { name: "Digital detox (1 hour)", timeOfDay: 'evening', goal: 30 },
    ],
    flexible: [
      { name: "Time block calendar", timeOfDay: 'morning', goal: 30 },
      { name: "Limit social media", timeOfDay: 'anytime', goal: 30 },
      { name: "Single-task focus", timeOfDay: 'anytime', goal: 30 },
    ],
  },
  // Mindfulness habits
  mindfulness: {
    morning: [
      { name: "Morning meditation (10 min)", timeOfDay: 'morning', goal: 30 },
      { name: "Gratitude journaling", timeOfDay: 'morning', goal: 30 },
      { name: "Watch the sunrise", timeOfDay: 'morning', goal: 30 },
    ],
    afternoon: [
      { name: "Mindful break (5 min)", timeOfDay: 'afternoon', goal: 30 },
      { name: "Walking meditation", timeOfDay: 'afternoon', goal: 30 },
      { name: "Practice deep breathing", timeOfDay: 'afternoon', goal: 30 },
    ],
    evening: [
      { name: "Evening reflection", timeOfDay: 'evening', goal: 30 },
      { name: "Connect with loved ones", timeOfDay: 'evening', goal: 30 },
      { name: "Relaxing bedtime routine", timeOfDay: 'evening', goal: 30 },
    ],
    flexible: [
      { name: "Meditate (10 min)", timeOfDay: 'anytime', goal: 30 },
      { name: "Digital detox (1 hour)", timeOfDay: 'anytime', goal: 30 },
      { name: "Practice gratitude", timeOfDay: 'anytime', goal: 30 },
    ],
  },
  // Learning habits
  learning: {
    morning: [
      { name: "Read (30 min)", timeOfDay: 'morning', goal: 30 },
      { name: "Educational podcast", timeOfDay: 'morning', goal: 30 },
      { name: "Morning journaling", timeOfDay: 'morning', goal: 30 },
    ],
    afternoon: [
      { name: "Learn new skill (30 min)", timeOfDay: 'afternoon', goal: 30 },
      { name: "Online course lesson", timeOfDay: 'afternoon', goal: 30 },
      { name: "Practice creativity", timeOfDay: 'afternoon', goal: 30 },
    ],
    evening: [
      { name: "Read before bed", timeOfDay: 'evening', goal: 30 },
      { name: "Reflect on lessons learned", timeOfDay: 'evening', goal: 30 },
      { name: "Practice hobby (30 min)", timeOfDay: 'evening', goal: 30 },
    ],
    flexible: [
      { name: "Learn something new", timeOfDay: 'anytime', goal: 30 },
      { name: "Listen to audiobook", timeOfDay: 'anytime', goal: 30 },
      { name: "Write or journal", timeOfDay: 'anytime', goal: 30 },
    ],
  },
  // Finance habits
  finance: {
    morning: [
      { name: "Check financial dashboard", timeOfDay: 'morning', goal: 30 },
      { name: "Review yesterday's spending", timeOfDay: 'morning', goal: 30 },
    ],
    afternoon: [
      { name: "Log expenses", timeOfDay: 'afternoon', goal: 30 },
      { name: "Review budget", timeOfDay: 'afternoon', goal: 30 },
    ],
    evening: [
      { name: "Track daily spending", timeOfDay: 'evening', goal: 30 },
      { name: "Review financial goals", timeOfDay: 'evening', goal: 30 },
    ],
    flexible: [
      { name: "Track spending", timeOfDay: 'anytime', goal: 30 },
      { name: "No impulse purchases", timeOfDay: 'anytime', goal: 30 },
      { name: "Weekly budget review", timeOfDay: 'anytime', goal: 30 },
    ],
  },
};

// ============================================================
// PREFERENCE-BASED TASK TEMPLATES  
// ============================================================

const TASK_SUGGESTIONS = {
  health: [
    { title: "Schedule dentist appointment", priority: 'medium', category: 'health' },
    { title: "Schedule annual checkup", priority: 'medium', category: 'health' },
    { title: "Refill prescriptions", priority: 'high', category: 'health' },
    { title: "Research gym memberships", priority: 'low', category: 'health' },
  ],
  productivity: [
    { title: "Set up task management system", priority: 'high', category: 'productivity' },
    { title: "Block focus time on calendar", priority: 'medium', category: 'productivity' },
    { title: "Organize digital files", priority: 'low', category: 'productivity' },
    { title: "Unsubscribe from spam emails", priority: 'low', category: 'productivity' },
  ],
  mindfulness: [
    { title: "Download meditation app", priority: 'low', category: 'mindfulness' },
    { title: "Buy a gratitude journal", priority: 'low', category: 'mindfulness' },
    { title: "Call a friend you miss", priority: 'medium', category: 'family' },
    { title: "Add plants to workspace", priority: 'low', category: 'mindfulness' },
  ],
  learning: [
    { title: "Start reading list", priority: 'low', category: 'learning' },
    { title: "Enroll in online course", priority: 'medium', category: 'learning' },
    { title: "Create learning goals", priority: 'medium', category: 'learning' },
    { title: "Subscribe to podcasts", priority: 'low', category: 'learning' },
  ],
  finance: [
    { title: "File taxes", priority: 'high', category: 'finance' },
    { title: "Pay credit card bills", priority: 'high', category: 'finance' },
    { title: "Create monthly budget", priority: 'high', category: 'finance' },
    { title: "Review subscriptions", priority: 'medium', category: 'finance' },
  ],
  // Default tasks for everyone
  general: [
    { title: "Grocery shopping", priority: 'medium', category: 'errands' },
    { title: "Do laundry", priority: 'medium', category: 'chores' },
    { title: "Clean living space", priority: 'medium', category: 'chores' },
  ],
};

// ============================================================
// ANIMATED COMPONENTS
// ============================================================

// Animated option button with scale and ripple effect
const OptionButton = ({ selected, onClick, children, colors, icon: Icon, subtitle }) => {
  const [ripple, setRipple] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    setRipple(true);
    setTimeout(() => setRipple(false), 300);
    onClick();
  };

  return (
    <button
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="w-full min-h-[60px] py-3 px-1 text-left transition-colors duration-150 flex items-center gap-4 relative overflow-hidden group"
      style={{
        backgroundColor: isHovered ? colors.bgTertiary : 'transparent',
        borderBottom: `1px solid ${colors.border}`,
      }}
      aria-pressed={selected}
    >
      {/* Ripple effect */}
      {ripple && (
        <div 
          className="absolute inset-0 animate-ripple"
          style={{ backgroundColor: `${colors.accent}30` }}
        />
      )}
      {Icon && (
        <Icon size={20} strokeWidth={1.5} className="flex-shrink-0" style={{ color: selected ? colors.accent : colors.textSecondary }} />
      )}
      <div className="flex-1 relative z-10">
        <span className="block font-medium" style={{ color: colors.textPrimary }}>
          {children}
        </span>
        {subtitle && (
          <span className="text-sm" style={{ color: colors.textSecondary }}>
            {subtitle}
          </span>
        )}
      </div>
      <div
        className="w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 transition-colors duration-150"
        style={{
          borderColor: selected ? colors.accent : colors.border,
          backgroundColor: selected ? colors.accent : 'transparent',
        }}
      >
        {selected && <Check size={12} strokeWidth={3} color={colors.onAccent} />}
      </div>
    </button>
  );
};

// Animated habit/task card with smooth expand
const SelectionCard = ({ items, categoryName, categoryIcon: CategoryIcon, categoryColor, selectedItems, onToggleItem, onToggleAll, colors, isExpanded, onToggleExpand }) => {
  const [contentHeight, setContentHeight] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const contentRef = useRef(null);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [items]);

  const allSelected = items.every((_, i) => selectedItems.has(i));
  const someSelected = items.some((_, i) => selectedItems.has(i));

  return (
    <div 
      className="rounded-lg overflow-hidden transition-all duration-150 ease-out"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ 
        backgroundColor: colors.bgSecondary,
        border: `1px solid ${someSelected || isExpanded ? colors.textMuted : colors.border}`,
      }}
    >
      {/* Category header */}
      <button
        onClick={onToggleExpand}
        className="w-full px-5 py-4 flex items-center gap-4 transition-all duration-150"
        style={{ 
          backgroundColor: isHovered && !isExpanded ? colors.bgTertiary : 'transparent',
        }}
      >
        {CategoryIcon && <CategoryIcon size={22} strokeWidth={1.5} className="flex-shrink-0" style={{ color: categoryColor }} />}
        <div className="flex-1 text-left">
          <h3 className="font-semibold text-lg transition-colors duration-150" style={{ color: colors.textPrimary }}>
            {categoryName}
          </h3>
          <p className="text-sm transition-all duration-150" style={{ color: colors.textSecondary }}>
            {someSelected ? `${[...selectedItems].length} selected` : isExpanded ? 'Select items below' : 'Tap to expand'}
          </p>
        </div>
        <ChevronRight 
          size={20} 
          className="transition-all duration-150"
          style={{ 
            color: colors.textMuted,
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
          }} 
        />
      </button>

      {/* Expandable content with spring animation */}
      <div 
        className="overflow-hidden transition-all duration-400 ease-out"
        style={{ 
          maxHeight: isExpanded ? `${contentHeight + 20}px` : '0px',
          opacity: isExpanded ? 1 : 0,
          transform: isExpanded ? 'translateY(0)' : 'translateY(-10px)',
        }}
      >
        <div ref={contentRef} className="px-4 pb-4 space-y-2">
          {/* Select all toggle */}
          <button
            onClick={() => onToggleAll(items)}
            className="w-full px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 flex items-center gap-2 "
            style={{ 
              backgroundColor: colors.bgTertiary,
              color: colors.textSecondary,
            }}
          >
            <div 
              className="w-4 h-4 rounded border flex items-center justify-center transition-all duration-150"
              style={{ 
                borderColor: allSelected ? colors.accent : colors.border,
                backgroundColor: allSelected ? colors.accent : 'transparent',
              }}
            >
              {allSelected && <Check size={10} strokeWidth={3} color={colors.onAccent} />}
            </div>
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>

          {/* Individual items with staggered animation and hover effects */}
          {items.map((item, index) => {
            const isSelected = selectedItems.has(index);
            const isItemHovered = hoveredItem === index;
            const itemName = item.name || item.title;

            return (
              <button
                key={index}
                onClick={() => onToggleItem(index)}
                onMouseEnter={() => setHoveredItem(index)}
                onMouseLeave={() => setHoveredItem(null)}
                className="w-full px-4 py-3 rounded-lg flex items-center gap-3 transition-all duration-200"
                style={{
                  backgroundColor: isItemHovered ? colors.bgTertiary : 'transparent',
                  animationDelay: `${index * 50}ms`,
                }}
              >
                <div
                  className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200"
                  style={{
                    borderColor: isSelected ? colors.accent : colors.border,
                    backgroundColor: isSelected ? colors.accent : 'transparent',
                  }}
                >
                  {isSelected && <Check size={12} strokeWidth={3} color={colors.onAccent} />}
                </div>
                <span className="flex-1 text-left transition-colors duration-200" style={{ color: isSelected || isItemHovered ? colors.textPrimary : colors.textSecondary }}>
                  {itemName}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MAIN ONBOARDING COMPONENT
// ============================================================

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { addHabits, addTasks, addEvent, addGoal, updateCalendar, isDark, user, updateUserData } = useApp();
  const colors = getColors(isDark);

  // Free tier limits
  const FREE_LIMITS = {
    maxHabits: 5,
    maxTasks: 5,
    maxEvents: 10,
  };

  // Multi-step state
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [slideDirection, setSlideDirection] = useState('right'); // 'left' or 'right'
  const [isAnimating, setIsAnimating] = useState(false);
  
  // User preferences
  const [motivation, setMotivation] = useState(null);
  const [focusAreas, setFocusAreas] = useState(new Set());
  const [timePreference, setTimePreference] = useState(null);
  
  // Selected items - these will be filled based on preferences
  const [selectedHabits, setSelectedHabits] = useState(new Set());
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  
  // Events state
  const [events, setEvents] = useState([]);
  const [currentEventName, setCurrentEventName] = useState('');
  const [currentEventDate, setCurrentEventDate] = useState('');
  const [currentEventTime, setCurrentEventTime] = useState('');
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [goalSuggestionIndex, setGoalSuggestionIndex] = useState(0);
  const [calendarColor, setCalendarColor] = useState(CALENDAR_COLORS[0]); // Default mint
  
  // Custom input state
  const [customHabitName, setCustomHabitName] = useState('');
  const [customHabits, setCustomHabits] = useState([]);
  const [customTaskName, setCustomTaskName] = useState('');
  const [customTasks, setCustomTasks] = useState([]);
  const [taskDeadline, setTaskDeadline] = useState(null); // null = no deadline, or 'today', 'tomorrow', '3days', '1week'
  
  // Goal creation state (only for users who selected 'Achieve specific goals')
  const [goalName, setGoalName] = useState('');
  const [goalTargetDate, setGoalTargetDate] = useState('');
  const [goalMilestones, setGoalMilestones] = useState([]);
  const [currentMilestone, setCurrentMilestone] = useState('');
  const [goalLinkedHabitIndices, setGoalLinkedHabitIndices] = useState(new Set());
  

  
  // Billing preference for pricing step
  const [billingPeriod, setBillingPeriod] = useState('yearly');
  
  // Expanded categories for selection cards
  const [expandedHabitCategory, setExpandedHabitCategory] = useState(null);
  const [expandedTaskCategory, setExpandedTaskCategory] = useState(null);
  
  // Swipe detection
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const minSwipeDistance = 50;
  
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const deltaX = touchStartX.current - touchEndX;
    const deltaY = touchStartY.current - touchEndY;
    
    // Only trigger if horizontal swipe is dominant (not scrolling)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
      if (deltaX > 0) {
        // Swiped left → go forward
        handleNext();
      } else {
        // Swiped right → go back
        handleBack();
      }
    }
    
    touchStartX.current = null;
    touchStartY.current = null;
  };
  
  // Rotating suggestion animations
  useEffect(() => {
    const eventInterval = setInterval(() => {
      setSuggestionIndex(prev => (prev + 1) % EVENT_SUGGESTIONS.length);
    }, 3000);
    const goalInterval = setInterval(() => {
      setGoalSuggestionIndex(prev => (prev + 1) % GOAL_SUGGESTIONS.length);
    }, 3000);
    return () => {
      clearInterval(eventInterval);
      clearInterval(goalInterval);
    };
  }, []);

  // Get user's first name from displayName (set during signup/Google auth)
  const firstName = user?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || 'there';

  const STEPS = [
    'welcome',
    'motivation', 
    'focus',
    'time',
    'habits',
    'tasks',
    'goals',
    'events',
    'pricing',
    'success'
  ];

  const currentStep = STEPS[step];
  const totalSteps = STEPS.length - 1; // Exclude success from count
  
  // Pricing constants
  const monthlyPrice = 5;
  const yearlyPrice = 50;
  const yearlyMonthly = (yearlyPrice / 12).toFixed(2);
  const savings = (monthlyPrice * 12) - yearlyPrice;

  // Generate suggested habits based on preferences
  const getSuggestedHabits = () => {
    const habits = [];
    const time = timePreference || 'flexible';
    
    focusAreas.forEach(area => {
      if (HABIT_SUGGESTIONS[area] && HABIT_SUGGESTIONS[area][time]) {
        habits.push(...HABIT_SUGGESTIONS[area][time]);
      }
    });
    
    // If no focus areas selected, return a balanced mix
    if (habits.length === 0) {
      return [
        ...HABIT_SUGGESTIONS.health[time].slice(0, 2),
        ...HABIT_SUGGESTIONS.productivity[time].slice(0, 2),
        ...HABIT_SUGGESTIONS.mindfulness[time].slice(0, 2),
      ];
    }
    
    return habits;
  };

  // Generate suggested tasks based on preferences
  const getSuggestedTasks = () => {
    const tasks = [...TASK_SUGGESTIONS.general];
    
    focusAreas.forEach(area => {
      if (TASK_SUGGESTIONS[area]) {
        tasks.push(...TASK_SUGGESTIONS[area]);
      }
    });
    
    return tasks;
  };

  // Animation helpers
  const animateStep = (direction, newStep) => {
    setSlideDirection(direction);
    setIsAnimating(true);
    setTimeout(() => {
      setStep(newStep);
      setTimeout(() => setIsAnimating(false), 50);
    }, 200);
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      animateStep('right', step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      animateStep('left', step - 1);
    }
  };

  const handleSkip = () => {
    // Mark onboarding as completed even if skipped
    if (updateUserData) {
      updateUserData({ hasCompletedOnboarding: true });
    }
    navigate('/');
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      const suggestedHabits = getSuggestedHabits();
      const suggestedTasks = getSuggestedTasks();
      
      // Build array of habits to add with their original indices/keys for linking
      const habitsToAdd = [];
      const habitIndexMap = []; // Maps array index in habitsToAdd to original index/key
      
      // Collect selected suggested habits (respecting free tier limit)
      Array.from(selectedHabits).forEach(index => {
        if (suggestedHabits[index] && habitsToAdd.length < FREE_LIMITS.maxHabits) {
          habitsToAdd.push(suggestedHabits[index]);
          habitIndexMap.push(index); // Store original index
        }
      });
      
      // Add custom habits (respecting limit)
      customHabits.forEach((habit, i) => {
        if (habitsToAdd.length < FREE_LIMITS.maxHabits) {
          habitsToAdd.push({ name: habit, timeOfDay: 'anytime', goal: 30 });
          habitIndexMap.push(`custom-${i}`); // Store custom key
        }
      });

      // Add habits and get their created IDs
      let createdHabitIds = [];
      if (habitsToAdd.length > 0) {
        createdHabitIds = await addHabits(habitsToAdd);
      }

      // Calculate deadline date if set
      const getDeadlineDate = () => {
        if (!taskDeadline) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        switch (taskDeadline) {
          case 'today':
            return today.toISOString().split('T')[0];
          case 'tomorrow':
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            return tomorrow.toISOString().split('T')[0];
          case '3days':
            const threeDays = new Date(today);
            threeDays.setDate(threeDays.getDate() + 3);
            return threeDays.toISOString().split('T')[0];
          case '1week':
            const oneWeek = new Date(today);
            oneWeek.setDate(oneWeek.getDate() + 7);
            return oneWeek.toISOString().split('T')[0];
          default:
            return null;
        }
      };
      const deadlineDate = getDeadlineDate();

      // Collect and add selected tasks (respecting free tier limit)
      const tasksToAdd = [];
      selectedTasks.forEach(index => {
        if (suggestedTasks[index] && tasksToAdd.length < FREE_LIMITS.maxTasks) {
          tasksToAdd.push({
            ...suggestedTasks[index],
            dueDate: deadlineDate,
          });
        }
      });
      // Add custom tasks (respecting limit)
      customTasks.forEach(task => {
        if (tasksToAdd.length < FREE_LIMITS.maxTasks) {
          tasksToAdd.push({ 
            title: task, 
            priority: 'medium', 
            category: 'custom',
            dueDate: deadlineDate,
          });
        }
      });

      if (tasksToAdd.length > 0) {
        await addTasks(tasksToAdd);
      }

      // Update existing "My Calendar" with user-selected color
      await updateCalendar('default', {
        color: calendarColor.value || calendarColor,
      });

      // Add events to the default calendar (respecting limit)
      const eventsToAdd = events.slice(0, FREE_LIMITS.maxEvents);
      for (const event of eventsToAdd) {
        await addEvent({
          name: event.name,
          date: event.date,
          startTime: event.time || null,
          calendarId: 'default',
        });
      }

      // Add goal if user created one during onboarding
      if (goalName.trim()) {
        const milestoneTimestamp = Date.now();
        const milestonesToSave = goalMilestones.map((text, index) => ({
          id: `ms-${milestoneTimestamp}-${index}-${Math.random().toString(36).substr(2, 5)}`,
          title: text,
          completed: false,
        }));
        
        // Map linked habit indices/keys to actual habit IDs
        const linkedHabitIds = [];
        goalLinkedHabitIndices.forEach(key => {
          // Find the position in habitIndexMap that matches this key
          const mapIndex = habitIndexMap.indexOf(key);
          if (mapIndex !== -1 && createdHabitIds[mapIndex]) {
            linkedHabitIds.push(createdHabitIds[mapIndex]);
          }
        });
        
        console.log('[Onboarding] Creating goal with milestones and linked habits:', {
          goalName: goalName.trim(),
          goalMilestonesState: goalMilestones,
          milestonesToSave,
          linkedHabitIds,
        });
        await addGoal({
          title: goalName.trim(),
          targetDate: goalTargetDate || (() => {
            // Default to 3 months from now if not set
            const date = new Date();
            date.setMonth(date.getMonth() + 3);
            return date.toISOString().split('T')[0];
          })(),
          milestones: milestonesToSave,
          linkedHabitIds,
        });
      }

      // Mark onboarding as completed; keep the motivation so the assistant can use it
      if (updateUserData) {
        await updateUserData({
          hasCompletedOnboarding: true,
          ...(motivation ? { agentProfile: { motivation } } : {}),
        });
      }

      // Go to pricing step (second to last)
      animateStep('right', STEPS.indexOf('pricing'));
    } catch (error) {
      console.error('Error saving:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Get category color based on focus area
  const getCategoryColor = (area) => {
    const colorMap = {
      health: colors.accent,
      productivity: colors.info,
      mindfulness: colors.textSecondary,
      learning: colors.warning,
      finance: colors.textSecondary,
    };
    return colorMap[area] || colors.accent;
  };



  // Render based on current step
  const renderStep = () => {
    switch (currentStep) {
      case 'welcome':
        return (
          <div className="flex flex-col items-center text-center px-4">
            <Sparkles size={28} strokeWidth={1.25} className="mb-4" style={{ color: colors.textPrimary }} />
            <h1 
              className="text-2xl mb-2 font-semibold tracking-[-0.035em]"
              style={{ color: colors.textPrimary }}
            >
              Hi {firstName}! Welcome to <span style={{ fontFamily: fonts.serif, color: colors.accent }}>mind.</span>
            </h1>
            <p 
              className="mb-2 max-w-sm text-sm"
              style={{ color: colors.textMuted }}
            >
              Your personal command center for habits, tasks, goals, and events. All in one beautiful, distraction-free space.
            </p>
            <p 
              className="text-xs mb-4"
              style={{ color: colors.textMuted }}
            >
              This takes about 75 seconds on average
            </p>
            
            {/* Feature highlights */}
            <div className="grid grid-cols-2 gap-2 max-w-sm mb-4">
              {[
                { icon: Repeat, label: 'Habits', desc: 'Daily streaks', color: colors.accent },
                { icon: CheckSquare, label: 'Tasks', desc: 'Smart priority', color: colors.info },
                { icon: Target, label: 'Goals', desc: 'Milestones', color: colors.textSecondary },
                { icon: Calendar, label: 'Events', desc: 'Calendar sync', color: colors.warning },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div 
                    key={item.label}
                    className="p-2.5 rounded-md transition-all duration-150 "
                    style={{ 
                      backgroundColor: colors.bgSecondary,
                      border: `1px solid ${colors.border}`,
                      animation: `slideInUp 0.4s ease-out ${i * 0.1}s both`,
                    }}
                  >
                    <Icon size={18} style={{ color: item.color }} />
                    <p className="font-medium text-sm mt-1" style={{ color: colors.textPrimary }}>{item.label}</p>
                    <p className="text-xs" style={{ color: colors.textMuted }}>{item.desc}</p>
                  </div>
                );
              })}
            </div>
            {/* Swipe to start indicator (mobile) + Button (desktop) */}
            <div 
              className="flex flex-col items-center mt-6"
              style={{ animation: 'fadeInUp 1s ease-out 0.5s both' }}
            >
              {/* Desktop/tablet button */}
              <button
                onClick={handleNext}
                className="hidden sm:flex px-6 py-3 rounded-md font-semibold text-base transition-all duration-150 transform active:scale-95 items-center gap-2 mb-3"
                style={{ backgroundColor: colors.accent, color: colors.onAccent }}
              >
                Let's Go
                <ArrowRight size={18} />
              </button>
              {/* Mobile swipe indicator */}
              <div 
                className="flex sm:hidden items-center gap-2 px-4 py-2 rounded-md"
                style={{ border: `1px solid ${colors.border}` }}
              >
                <ChevronLeft size={16} style={{ color: colors.textMuted, animation: 'swipeFingerLeft 1.5s ease-in-out infinite' }} />
                <span className="text-sm" style={{ color: colors.textMuted }}>Swipe left to start</span>
              </div>
              <button
                onClick={handleSkip}
                className="mt-4 text-xs underline transition-opacity hover:opacity-70"
                style={{ color: colors.textMuted }}
              >
                Skip for now
              </button>
            </div>
          </div>
        );

      case 'motivation':
        return (
          <div className="px-6">
            <h2 
              className="text-[30px] sm:text-[40px] leading-[1.05] mb-2 font-semibold tracking-[-0.035em] max-w-md mx-auto w-full"
              style={{ color: colors.textPrimary }}
            >
              What brings you here?
            </h2>
            <p className="mb-8 max-w-md mx-auto w-full" style={{ color: colors.textSecondary }}>
              This helps us personalize your experience
            </p>
            <div className="max-w-md mx-auto" style={{ borderTop: `1px solid ${colors.textPrimary}` }}>
              {[
                { id: 'build', label: 'Build better habits', icon: Sprout, subtitle: 'Create positive routines' },
                { id: 'break', label: 'Break bad habits', icon: Unlock, subtitle: 'Replace unwanted patterns' },
                { id: 'track', label: 'Track my routines', icon: BarChart3, subtitle: 'Stay organized and consistent' },
                { id: 'goals', label: 'Achieve specific goals', icon: Target, subtitle: 'Work toward something big' },
                { id: 'explore', label: 'Just exploring', icon: Eye, subtitle: 'See what this app can do' },
              ].map((option, i) => (
                <div
                  key={option.id}
                  style={{ 
                    animation: `slideInRight 0.4s ease-out ${i * 0.1}s both`,
                  }}
                >
                  <OptionButton
                    selected={motivation === option.id}
                    onClick={() => {
                      setMotivation(option.id);
                      // Auto-advance after short delay for visual feedback
                      setTimeout(() => handleNext(), 300);
                    }}
                    colors={colors}
                    icon={option.icon}
                    subtitle={option.subtitle}
                  >
                    {option.label}
                  </OptionButton>
                </div>
              ))}
            </div>
          </div>
        );

      case 'focus':
        return (
          <div className="px-6">
            <h2 
              className="text-[30px] sm:text-[40px] leading-[1.05] mb-2 font-semibold tracking-[-0.035em] max-w-md mx-auto w-full"
              style={{ color: colors.textPrimary }}
            >
              What areas matter most?
            </h2>
            <p className="mb-8 max-w-md mx-auto w-full" style={{ color: colors.textSecondary }}>
              Select up to 3 focus areas
            </p>
            <div className="max-w-md mx-auto" style={{ borderTop: `1px solid ${colors.textPrimary}` }}>
              {[
                { id: 'health', label: 'Health & Fitness', icon: Dumbbell },
                { id: 'productivity', label: 'Productivity', icon: Zap },
                { id: 'mindfulness', label: 'Mindfulness & Balance', icon: Brain },
                { id: 'learning', label: 'Learning & Growth', icon: BookOpen },
                { id: 'finance', label: 'Finance & Organization', icon: Wallet },
              ].map((option, i) => (
                <div
                  key={option.id}
                  style={{ 
                    animation: `slideInRight 0.4s ease-out ${i * 0.1}s both`,
                  }}
                >
                  <OptionButton
                    selected={focusAreas.has(option.id)}
                    onClick={() => {
                      const newAreas = new Set(focusAreas);
                      if (newAreas.has(option.id)) {
                        newAreas.delete(option.id);
                      } else if (newAreas.size < 3) {
                        newAreas.add(option.id);
                      }
                      setFocusAreas(newAreas);
                    }}
                    colors={colors}
                    icon={option.icon}
                  >
                    {option.label}
                  </OptionButton>
                </div>
              ))}
            </div>
            <p className="text-center mt-4 text-sm transition-all duration-150" style={{ color: colors.textMuted }}>
              <span style={{ color: focusAreas.size === 3 ? colors.accent : colors.textMuted }}>
                {focusAreas.size}/3 selected
              </span>
            </p>
            
            {/* Desktop Continue Button */}
            {focusAreas.size > 0 && (
              <div className="hidden sm:flex justify-center mt-6">
                <button
                  onClick={handleNext}
                  className="px-8 py-3 rounded-full font-semibold transition-all duration-150 transform active:scale-95 flex items-center gap-2"
                  style={{ backgroundColor: colors.accent, color: colors.onAccent }}
                >
                  Continue
                  <ArrowRight size={18} />
                </button>
              </div>
            )}
          </div>
        );

      case 'time':
        return (
          <div className="px-6">
            <h2 
              className="text-[30px] sm:text-[40px] leading-[1.05] mb-2 font-semibold tracking-[-0.035em] max-w-md mx-auto w-full"
              style={{ color: colors.textPrimary }}
            >
              When do you thrive?
            </h2>
            <p className="mb-8 max-w-md mx-auto w-full" style={{ color: colors.textSecondary }}>
              We'll suggest habits for your peak hours
            </p>
            <div className="max-w-md mx-auto" style={{ borderTop: `1px solid ${colors.textPrimary}` }}>
              {[
                { id: 'morning', label: 'Early bird', icon: Sunrise, subtitle: '5 AM – 12 PM' },
                { id: 'afternoon', label: 'Afternoon energy', icon: Sun, subtitle: '12 PM – 5 PM' },
                { id: 'evening', label: 'Night owl', icon: Moon, subtitle: '5 PM – Late' },
                { id: 'flexible', label: 'Whenever works', icon: Shuffle, subtitle: "I'm flexible" },
              ].map((option, i) => (
                <div
                  key={option.id}
                  style={{ 
                    animation: `slideInRight 0.4s ease-out ${i * 0.1}s both`,
                  }}
                >
                  <OptionButton
                    selected={timePreference === option.id}
                    onClick={() => {
                      setTimePreference(option.id);
                      // Auto-advance after short delay for visual feedback
                      setTimeout(() => handleNext(), 300);
                    }}
                    colors={colors}
                    icon={option.icon}
                    subtitle={option.subtitle}
                  >
                    {option.label}
                  </OptionButton>
                </div>
              ))}
            </div>
          </div>
        );

      case 'habits':
        const suggestedHabits = getSuggestedHabits();
        // Flatten all habits with their category info for pill display
        const allHabitPills = [];
        let habitIndex = 0;
        focusAreas.forEach(area => {
          const time = timePreference || 'flexible';
          if (HABIT_SUGGESTIONS[area] && HABIT_SUGGESTIONS[area][time]) {
            HABIT_SUGGESTIONS[area][time].forEach(habit => {
              allHabitPills.push({
                ...habit,
                area,
                globalIndex: habitIndex,
                color: getCategoryColor(area),
              });
              habitIndex++;
            });
          }
        });
        
        return (
          <div className="px-4">
            <div className="flex justify-center mb-3">
              <Repeat size={28} strokeWidth={1.25} style={{ color: colors.textPrimary }} />
            </div>
            <h2 
              className="text-[30px] sm:text-[40px] leading-[1.05] mb-2 font-semibold tracking-[-0.035em] max-w-md mx-auto w-full"
              style={{ color: colors.textPrimary }}
            >
              Tap habits you want to build
            </h2>
            <p className="mb-4 max-w-md mx-auto w-full" style={{ color: colors.textSecondary }}>
              Select up to {FREE_LIMITS.maxHabits} habits to track daily
            </p>
            
            {/* Habit Pills - Tap to Select */}
            <div className="flex flex-wrap gap-2 justify-center max-w-lg mx-auto mb-4">
              {allHabitPills.map((habit, idx) => {
                const isSelected = selectedHabits.has(habit.globalIndex);
                const atLimit = (selectedHabits.size + customHabits.length) >= FREE_LIMITS.maxHabits;
                const isDisabled = !isSelected && atLimit;
                
                return (
                  <button
                    key={habit.globalIndex}
                    onClick={() => {
                      const newSelected = new Set(selectedHabits);
                      if (isSelected) {
                        newSelected.delete(habit.globalIndex);
                      } else if (!atLimit) {
                        newSelected.add(habit.globalIndex);
                      }
                      setSelectedHabits(newSelected);
                    }}
                    disabled={isDisabled}
                    className="px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-150 flex items-center gap-2"
                    style={{
                      backgroundColor: isSelected ? `${habit.color}20` : colors.bgSecondary,
                      border: `1px solid ${isSelected ? habit.color : colors.border}`,
                      color: isSelected ? habit.color : isDisabled ? colors.textMuted : colors.textPrimary,
                      opacity: isDisabled ? 0.5 : 1,
                      animation: `slideInUp 0.4s ease-out ${idx * 0.05}s both`,
                    }}
                  >
                    <span>{habit.name}</span>
                  </button>
                );
              })}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 max-w-lg mx-auto my-4">
              <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
              <span className="text-xs" style={{ color: colors.textMuted }}>or add your own</span>
              <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
            </div>
            
            {/* Custom habit input - simplified */}
            <div className="max-w-lg mx-auto">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customHabitName}
                  onChange={(e) => setCustomHabitName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && customHabitName.trim()) {
                      const totalSelected = selectedHabits.size + customHabits.length;
                      if (totalSelected < FREE_LIMITS.maxHabits) {
                        setCustomHabits([...customHabits, customHabitName.trim()]);
                        setCustomHabitName('');
                      }
                    }
                  }}
                  placeholder="Type a custom habit..."
                  className="flex-1 px-4 py-3 rounded-full text-sm outline-none"
                  style={{ 
                    backgroundColor: colors.bgSecondary,
                    border: `1px solid ${colors.border}`,
                    color: colors.textPrimary,
                  }}
                />
                <button
                  onClick={() => {
                    if (customHabitName.trim()) {
                      const totalSelected = selectedHabits.size + customHabits.length;
                      if (totalSelected < FREE_LIMITS.maxHabits) {
                        setCustomHabits([...customHabits, customHabitName.trim()]);
                        setCustomHabitName('');
                      }
                    }
                  }}
                  disabled={!customHabitName.trim() || (selectedHabits.size + customHabits.length) >= FREE_LIMITS.maxHabits}
                  className="p-3 rounded-full transition-all duration-200"
                  style={{ 
                    backgroundColor: customHabitName.trim() ? colors.accent : colors.bgSecondary,
                    border: `1px solid ${customHabitName.trim() ? colors.accent : colors.border}`,
                    color: customHabitName.trim() ? colors.bg : colors.textMuted,
                    opacity: (selectedHabits.size + customHabits.length) >= FREE_LIMITS.maxHabits ? 0.5 : 1,
                  }}
                >
                  <Plus size={18} />
                </button>
              </div>
              
              {/* Custom habits as pills */}
              {customHabits.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3 justify-center">
                  {customHabits.map((habit, i) => (
                    <div 
                      key={i}
                      className="px-4 py-2 rounded-full flex items-center gap-2"
                      style={{ 
                        backgroundColor: `${colors.accent}20`,
                        border: `1px solid ${colors.accent}`,
                        animation: 'bounceIn 0.3s ease-out',
                      }}
                    >
                      <Check size={14} style={{ color: colors.accent }} />
                      <span className="text-sm font-medium" style={{ color: colors.accent }}>{habit}</span>
                      <button
                        onClick={() => setCustomHabits(customHabits.filter((_, idx) => idx !== i))}
                        className="p-0.5 rounded-full hover:opacity-70 ml-1"
                        style={{ backgroundColor: `${colors.accent}30` }}
                      >
                        <X size={12} style={{ color: colors.accent }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Selection count - floating badge style */}
            {(selectedHabits.size + customHabits.length) > 0 && (
              <div className="text-center mt-6">
                <div 
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
                  style={{ 
                    backgroundColor: (selectedHabits.size + customHabits.length) >= FREE_LIMITS.maxHabits 
                      ? `${colors.warning}20` 
                      : `${colors.accent}20`,
                    border: `1px solid ${(selectedHabits.size + customHabits.length) >= FREE_LIMITS.maxHabits ? colors.warning : colors.accent}`,
                  }}
                >
                  <Flame size={16} style={{ color: (selectedHabits.size + customHabits.length) >= FREE_LIMITS.maxHabits ? colors.warning : colors.accent }} />
                  <span 
                    className="font-semibold text-sm"
                    style={{ color: (selectedHabits.size + customHabits.length) >= FREE_LIMITS.maxHabits ? colors.warning : colors.accent }}
                  >
                    {selectedHabits.size + customHabits.length}/{FREE_LIMITS.maxHabits} habits
                    {(selectedHabits.size + customHabits.length) >= FREE_LIMITS.maxHabits && ' (limit reached)'}
                  </span>
                </div>
              </div>
            )}
            
            {/* Desktop Continue Button */}
            <div className="hidden sm:flex justify-center mt-6">
              <button
                onClick={handleNext}
                className="px-8 py-3 rounded-full font-semibold transition-all duration-150 transform active:scale-95 flex items-center gap-2"
                style={{ backgroundColor: colors.accent, color: colors.onAccent }}
              >
                Continue
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        );

      case 'tasks':
        const suggestedTasks = getSuggestedTasks();
        // Pastel priority colors - subtle and soft
        const priorityColors = { 
          high: '#F9A8A8',    // Soft coral/rose
          medium: '#FCD88A',  // Soft amber/peach
          low: '#A8E6CF'      // Soft mint/sage
        };
        
        return (
          <div className="px-4">
            <div className="flex justify-center mb-3">
              <CheckSquare size={28} strokeWidth={1.25} style={{ color: colors.info }} />
            </div>
            <h2 
              className="text-[30px] sm:text-[40px] leading-[1.05] mb-2 font-semibold tracking-[-0.035em] max-w-md mx-auto w-full"
              style={{ color: colors.textPrimary }}
            >
              Tackle your to-dos
            </h2>
            <p className="text-center mb-4 text-sm" style={{ color: colors.textSecondary }}>
              Tap to select tasks. Smart urgency scoring auto-sorts by priority.
            </p>
            
            {/* Task pills - tap to select */}
            <div className="flex flex-wrap gap-2 justify-center max-w-lg mx-auto mb-4">
              {suggestedTasks.map((task, idx) => {
                const isSelected = selectedTasks.has(idx);
                const totalSelected = selectedTasks.size + customTasks.length;
                const isDisabled = !isSelected && totalSelected >= FREE_LIMITS.maxTasks;
                
                return (
                  <button
                    key={idx}
                    onClick={() => {
                      const newSelected = new Set(selectedTasks);
                      if (isSelected) {
                        newSelected.delete(idx);
                      } else if (totalSelected < FREE_LIMITS.maxTasks) {
                        newSelected.add(idx);
                      }
                      setSelectedTasks(newSelected);
                    }}
                    disabled={isDisabled}
                    className="px-3 py-2 rounded-full text-sm font-medium transition-all duration-150 flex items-center gap-1.5"
                    style={{
                      backgroundColor: isSelected ? `${colors.info}20` : colors.bgSecondary,
                      border: `1px solid ${isSelected ? colors.info : colors.border}`,
                      color: isSelected ? colors.info : isDisabled ? colors.textMuted : colors.textPrimary,
                      opacity: isDisabled ? 0.5 : 1,
                      animation: `slideInUp 0.4s ease-out ${idx * 0.03}s both`,
                    }}
                  >
                    <span 
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: priorityColors[task.priority] }}
                    />
                    <span>{task.title}</span>
                  </button>
                );
              })}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 max-w-lg mx-auto my-4">
              <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
              <span className="text-xs" style={{ color: colors.textMuted }}>or add your own</span>
              <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
            </div>
            
            {/* Custom task input */}
            <div className="max-w-lg mx-auto">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customTaskName}
                  onChange={(e) => setCustomTaskName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && customTaskName.trim()) {
                      const totalSelected = selectedTasks.size + customTasks.length;
                      if (totalSelected < FREE_LIMITS.maxTasks) {
                        setCustomTasks([...customTasks, customTaskName.trim()]);
                        setCustomTaskName('');
                      }
                    }
                  }}
                  placeholder="Type a custom task..."
                  className="flex-1 px-4 py-3 rounded-full text-sm outline-none"
                  style={{ 
                    backgroundColor: colors.bgSecondary,
                    border: `1px solid ${colors.border}`,
                    color: colors.textPrimary,
                  }}
                />
                <button
                  onClick={() => {
                    if (customTaskName.trim()) {
                      const totalSelected = selectedTasks.size + customTasks.length;
                      if (totalSelected < FREE_LIMITS.maxTasks) {
                        setCustomTasks([...customTasks, customTaskName.trim()]);
                        setCustomTaskName('');
                      }
                    }
                  }}
                  disabled={!customTaskName.trim() || (selectedTasks.size + customTasks.length) >= FREE_LIMITS.maxTasks}
                  className="p-3 rounded-full transition-all duration-200"
                  style={{ 
                    backgroundColor: customTaskName.trim() ? colors.info : colors.bgSecondary,
                    border: `1px solid ${customTaskName.trim() ? colors.info : colors.border}`,
                    color: customTaskName.trim() ? colors.bg : colors.textMuted,
                    opacity: (selectedTasks.size + customTasks.length) >= FREE_LIMITS.maxTasks ? 0.5 : 1,
                  }}
                >
                  <Plus size={18} />
                </button>
              </div>
              
              {/* Custom tasks as pills */}
              {customTasks.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3 justify-center">
                  {customTasks.map((task, i) => (
                    <div 
                      key={i}
                      className="px-3 py-2 rounded-full flex items-center gap-2"
                      style={{ 
                        backgroundColor: `${colors.info}20`,
                        border: `1px solid ${colors.info}`,
                        animation: 'bounceIn 0.3s ease-out',
                      }}
                    >
                      <Check size={14} style={{ color: colors.info }} />
                      <span className="text-sm font-medium" style={{ color: colors.info }}>{task}</span>
                      <button
                        onClick={() => setCustomTasks(customTasks.filter((_, idx) => idx !== i))}
                        className="p-0.5 rounded-full hover:opacity-70 ml-1"
                        style={{ backgroundColor: `${colors.info}30` }}
                      >
                        <X size={12} style={{ color: colors.info }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Selection count - floating badge style */}
            {(selectedTasks.size + customTasks.length) > 0 && (
              <div className="text-center mt-4">
                <div 
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full"
                  style={{ 
                    backgroundColor: (selectedTasks.size + customTasks.length) >= FREE_LIMITS.maxTasks 
                      ? `${colors.warning}20` 
                      : `${colors.info}20`,
                    border: `1px solid ${(selectedTasks.size + customTasks.length) >= FREE_LIMITS.maxTasks ? colors.warning : colors.info}`,
                  }}
                >
                  <CheckSquare size={16} style={{ color: (selectedTasks.size + customTasks.length) >= FREE_LIMITS.maxTasks ? colors.warning : colors.info }} />
                  <span 
                    className="font-semibold text-sm"
                    style={{ color: (selectedTasks.size + customTasks.length) >= FREE_LIMITS.maxTasks ? colors.warning : colors.info }}
                  >
                    {selectedTasks.size + customTasks.length}/{FREE_LIMITS.maxTasks} tasks
                    {(selectedTasks.size + customTasks.length) >= FREE_LIMITS.maxTasks && ' (limit reached)'}
                  </span>
                </div>
              </div>
            )}
            
            {/* Desktop Continue Button */}
            <div className="hidden sm:flex justify-center mt-6">
              <button
                onClick={handleNext}
                className="px-8 py-3 rounded-full font-semibold transition-all duration-150 transform active:scale-95 flex items-center gap-2"
                style={{ backgroundColor: colors.info, color: colors.bg }}
              >
                Continue
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        );

      case 'goals':
        const handleAddMilestone = () => {
          if (currentMilestone.trim() && goalMilestones.length < 5) {
            setGoalMilestones([...goalMilestones, currentMilestone.trim()]);
            setCurrentMilestone('');
          }
        };

        const handleRemoveMilestone = (index) => {
          setGoalMilestones(goalMilestones.filter((_, i) => i !== index));
        };

        // Calculate default target date (3 months from now)
        const getDefaultTargetDate = () => {
          const date = new Date();
          date.setMonth(date.getMonth() + 3);
          return date.toISOString().split('T')[0];
        };

        return (
          <div className="px-6">
            <div className="flex justify-center mb-3">
              <Target size={28} strokeWidth={1.25} style={{ color: colors.textSecondary }} />
            </div>
            <h2 
              className="text-[30px] sm:text-[40px] leading-[1.05] mb-2 font-semibold tracking-[-0.035em] max-w-md mx-auto w-full"
              style={{ color: colors.textPrimary }}
            >
              Set your first goal
            </h2>
            <p className="mb-4 max-w-md mx-auto w-full" style={{ color: colors.textSecondary }}>
              What's something meaningful you want to achieve?
            </p>
            
            <div className="max-w-md mx-auto space-y-4">
                  {/* Goal creation form */}
                  <div 
                    className="p-4 rounded-lg space-y-4"
                    style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
                  >
                    {/* Goal name */}
                    <div>
                      <label className="text-xs font-medium mb-1.5 block" style={{ color: colors.textMuted }}>
                        What's your goal?
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={goalName}
                          onChange={(e) => setGoalName(e.target.value)}
                          placeholder=" "
                          className="w-full px-4 py-3 rounded-md text-base outline-none transition-all duration-150"
                          style={{ 
                            backgroundColor: colors.bgTertiary,
                            border: `1px solid ${goalName ? colors.textSecondary : colors.border}`,
                            color: colors.textPrimary,
                          }}
                        />
                        {/* Animated placeholder */}
                        {!goalName && (
                          <div 
                            className="absolute inset-0 flex items-center px-4 pointer-events-none overflow-hidden"
                          >
                            <span 
                              key={goalSuggestionIndex}
                              className="text-base transition-all duration-200"
                              style={{ 
                                color: colors.textMuted,
                                animation: 'fadeInUp 0.5s ease-out',
                              }}
                            >
                              {GOAL_SUGGESTIONS[goalSuggestionIndex].name}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Target date */}
                    <div>
                      <label className="text-xs font-medium mb-1.5 block" style={{ color: colors.textMuted }}>
                        When do you want to achieve this?
                      </label>
                      <input
                        type="date"
                        value={goalTargetDate}
                        onChange={(e) => setGoalTargetDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 rounded-md text-base outline-none transition-all duration-150"
                        style={{ 
                          backgroundColor: colors.bgTertiary,
                          border: `1px solid ${goalTargetDate ? colors.textSecondary : colors.border}`,
                          color: colors.textPrimary,
                        }}
                      />
                      {!goalTargetDate && (
                        <div className="flex gap-2 mt-2">
                          {[
                            { label: '1 month', months: 1 },
                            { label: '3 months', months: 3 },
                            { label: '6 months', months: 6 },
                            { label: '1 year', months: 12 },
                          ].map((option) => (
                            <button
                              key={option.months}
                              onClick={() => {
                                const date = new Date();
                                date.setMonth(date.getMonth() + option.months);
                                setGoalTargetDate(date.toISOString().split('T')[0]);
                              }}
                              className="flex-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 "
                              style={{
                                backgroundColor: colors.bgTertiary,
                                color: colors.textSecondary,
                                border: `1px solid ${colors.border}`,
                              }}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Milestones */}
                    <div>
                      <label className="text-xs font-medium mb-1.5 block" style={{ color: colors.textMuted }}>
                        Break it into milestones <span style={{ color: colors.textMuted }}>(optional)</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={currentMilestone}
                          onChange={(e) => setCurrentMilestone(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAddMilestone();
                          }}
                          placeholder="e.g., Run 5K, Complete first lesson"
                          className="flex-1 px-3 py-2 rounded-lg text-sm outline-none"
                          style={{ 
                            backgroundColor: colors.bgTertiary,
                            border: `1px solid ${colors.border}`,
                            color: colors.textPrimary,
                          }}
                        />
                        <button
                          onClick={handleAddMilestone}
                          disabled={!currentMilestone.trim() || goalMilestones.length >= 5}
                          className="px-3 py-2 rounded-lg transition-all duration-200"
                          style={{ 
                            backgroundColor: currentMilestone.trim() ? colors.textSecondary : colors.bgTertiary,
                            color: currentMilestone.trim() ? colors.bg : colors.textMuted,
                            opacity: goalMilestones.length >= 5 ? 0.5 : 1,
                          }}
                        >
                          <Plus size={18} />
                        </button>
                      </div>
                      {goalMilestones.length > 0 && (
                        <div className="mt-2 space-y-1.5">
                          {goalMilestones.map((milestone, i) => (
                            <div 
                              key={i}
                              className="flex items-center justify-between px-3 py-2 rounded-lg"
                              style={{ backgroundColor: `${colors.textSecondary}15` }}
                            >
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium"
                                  style={{ backgroundColor: `${colors.textSecondary}30`, color: colors.textSecondary }}
                                >
                                  {i + 1}
                                </div>
                                <span className="text-sm" style={{ color: colors.textPrimary }}>{milestone}</span>
                              </div>
                              <button
                                onClick={() => handleRemoveMilestone(i)}
                                className="p-1 rounded hover:opacity-70"
                              >
                                <X size={14} style={{ color: colors.textMuted }} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {goalMilestones.length < 5 && goalMilestones.length > 0 && (
                        <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                          {5 - goalMilestones.length} more milestones available
                        </p>
                      )}
                    </div>
                    
                    {/* Link Habits Section */}
                    {(selectedHabits.size > 0 || customHabits.length > 0) && (
                      <div>
                        <label className="text-xs font-medium mb-1.5 flex items-center gap-1" style={{ color: colors.textMuted }}>
                          <Link2 size={12} />
                          Link habits to track consistency <span style={{ color: colors.textMuted }}>(optional)</span>
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {/* Show selected suggested habits */}
                          {Array.from(selectedHabits).map(index => {
                            const suggestedHabits = getSuggestedHabits();
                            const habit = suggestedHabits[index];
                            if (!habit) return null;
                            const isLinked = goalLinkedHabitIndices.has(index);
                            return (
                              <button
                                key={`suggested-${index}`}
                                type="button"
                                onClick={() => {
                                  const newLinked = new Set(goalLinkedHabitIndices);
                                  if (isLinked) {
                                    newLinked.delete(index);
                                  } else {
                                    newLinked.add(index);
                                  }
                                  setGoalLinkedHabitIndices(newLinked);
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all duration-200"
                                style={{
                                  backgroundColor: isLinked ? colors.textSecondary : colors.bgTertiary,
                                  color: isLinked ? colors.bg : colors.textSecondary,
                                  border: `1px solid ${isLinked ? colors.textSecondary : colors.border}`
                                }}
                              >
                                {isLinked && <Check size={12} />}
                                <Flame size={12} />
                                {habit.name}
                              </button>
                            );
                          })}
                          {/* Show custom habits */}
                          {customHabits.map((habitName, index) => {
                            const customKey = `custom-${index}`;
                            const isLinked = goalLinkedHabitIndices.has(customKey);
                            return (
                              <button
                                key={customKey}
                                type="button"
                                onClick={() => {
                                  const newLinked = new Set(goalLinkedHabitIndices);
                                  if (isLinked) {
                                    newLinked.delete(customKey);
                                  } else {
                                    newLinked.add(customKey);
                                  }
                                  setGoalLinkedHabitIndices(newLinked);
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all duration-200"
                                style={{
                                  backgroundColor: isLinked ? colors.textSecondary : colors.bgTertiary,
                                  color: isLinked ? colors.bg : colors.textSecondary,
                                  border: `1px solid ${isLinked ? colors.textSecondary : colors.border}`
                                }}
                              >
                                {isLinked && <Check size={12} />}
                                <Flame size={12} />
                                {habitName}
                              </button>
                            );
                          })}
                        </div>
                        {goalLinkedHabitIndices.size > 0 && (
                          <p className="text-xs mt-2" style={{ color: colors.textMuted }}>
                            {goalLinkedHabitIndices.size} habit{goalLinkedHabitIndices.size > 1 ? 's' : ''} linked — consistency will be tracked
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Skip option */}
                  <p className="text-center text-sm" style={{ color: colors.textMuted }}>
                    You can skip this and create goals later
                  </p>
            </div>
            
            {/* Desktop Continue Button */}
            <div className="hidden sm:flex justify-center mt-6">
              <button
                onClick={handleNext}
                className="px-8 py-3 rounded-full font-semibold transition-all duration-150 transform active:scale-95 flex items-center gap-2"
                style={{ backgroundColor: colors.textSecondary, color: colors.bg }}
              >
                Continue
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        );

      case 'events':
        const currentSuggestion = EVENT_SUGGESTIONS[suggestionIndex];
        
        const handleAddEvent = () => {
          if (!currentEventName.trim() || !currentEventDate) return;
          setEvents([...events, {
            name: currentEventName.trim(),
            date: currentEventDate,
            time: currentEventTime || null,
          }]);
          setCurrentEventName('');
          setCurrentEventDate('');
          setCurrentEventTime('');
        };

        const handleRemoveEvent = (index) => {
          setEvents(events.filter((_, i) => i !== index));
        };

        return (
          <div className="px-6">
            <div className="flex justify-center mb-4">
              <Calendar size={28} strokeWidth={1.25} style={{ color: calendarColor }} />
            </div>
            <h2 
              className="text-[30px] sm:text-[40px] leading-[1.05] mb-2 font-semibold tracking-[-0.035em] max-w-md mx-auto w-full"
              style={{ color: colors.textPrimary }}
            >
              Schedule your life
            </h2>
            <p className="mb-4 max-w-md mx-auto w-full" style={{ color: colors.textSecondary }}>
              Add appointments, meetings, and activities.
            </p>

            <div className="max-w-md mx-auto space-y-4">
              {/* Calendar color picker */}
              <div 
                className="p-4 rounded-lg"
                style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
              >
                <p className="text-sm font-medium mb-3" style={{ color: colors.textPrimary }}>
                  Choose your calendar color
                </p>
                <div className="flex flex-wrap gap-2">
                  {CALENDAR_COLORS.slice(0, 12).map((color) => (
                    <button
                      key={color}
                      onClick={() => setCalendarColor(color)}
                      className="w-8 h-8 rounded-full transition-all duration-200 "
                      style={{ 
                        backgroundColor: color,
                        border: calendarColor === color ? '3px solid white' : 'none',
                        boxShadow: calendarColor === color ? `0 0 0 2px ${color}` : 'none',
                        transform: calendarColor === color ? 'scale(1.15)' : 'scale(1)',
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Event input form with calendar color glow */}
              <div 
                className="p-4 rounded-lg space-y-3 transition-all duration-150"
                style={{ 
                  backgroundColor: colors.bgSecondary, 
                  border: `1px solid ${calendarColor}40`,
                  boxShadow: `0 0 20px ${calendarColor}15`,
                }}
              >
                {/* Event name with rotating suggestion */}
                <div className="relative">
                  <input
                    type="text"
                    value={currentEventName}
                    onChange={(e) => setCurrentEventName(e.target.value)}
                    className="w-full px-4 py-3 rounded-md text-base outline-none transition-all duration-150"
                    style={{ 
                      backgroundColor: colors.bgTertiary,
                      border: `1px solid ${currentEventName ? calendarColor : colors.border}`,
                      color: colors.textPrimary,
                    }}
                    placeholder=" "
                  />
                  {/* Animated placeholder */}
                  {!currentEventName && (
                    <div 
                      className="absolute inset-0 flex items-center px-4 pointer-events-none overflow-hidden"
                    >
                      <span 
                        key={suggestionIndex}
                        className="text-base transition-all duration-200"
                        style={{ 
                          color: colors.textMuted,
                          animation: 'fadeInUp 0.5s ease-out',
                        }}
                      >
                        {currentSuggestion.name}
                      </span>
                    </div>
                  )}
                </div>

                {/* Date and Time row */}
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-xs font-medium mb-1 block" style={{ color: colors.textMuted }}>
                      Date
                    </label>
                    <input
                      type="date"
                      value={currentEventDate}
                      onChange={(e) => setCurrentEventDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-all duration-150"
                      style={{ 
                        backgroundColor: colors.bgTertiary,
                        border: `1px solid ${currentEventDate ? calendarColor : colors.border}`,
                        color: colors.textPrimary,
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs font-medium mb-1 block" style={{ color: colors.textMuted }}>
                      Time <span style={{ color: colors.textMuted }}>(optional)</span>
                    </label>
                    <input
                      type="time"
                      value={currentEventTime}
                      onChange={(e) => setCurrentEventTime(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg text-sm outline-none transition-all duration-150"
                      style={{ 
                        backgroundColor: colors.bgTertiary,
                        border: `1px solid ${currentEventTime ? calendarColor : colors.border}`,
                        color: colors.textPrimary,
                      }}
                    />
                  </div>
                </div>

                {/* Add button */}
                <button
                  onClick={handleAddEvent}
                  disabled={!currentEventName.trim() || !currentEventDate || events.length >= FREE_LIMITS.maxEvents}
                  className="w-full py-3 rounded-md font-medium flex items-center justify-center gap-2 transition-all duration-150 transform active:scale-[0.98]"
                  style={{ 
                    backgroundColor: currentEventName.trim() && currentEventDate && events.length < FREE_LIMITS.maxEvents ? calendarColor : colors.bgTertiary,
                    color: currentEventName.trim() && currentEventDate && events.length < FREE_LIMITS.maxEvents ? colors.bg : colors.textMuted,
                  }}
                >
                  <Plus size={18} />
                  Add Event
                </button>
              </div>

              {/* Added events list */}
              {events.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium" style={{ color: colors.textSecondary }}>
                    Added events ({events.length}/{FREE_LIMITS.maxEvents}):
                  </p>
                  {events.map((event, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-3 p-3 rounded-md transition-all duration-150"
                      style={{ 
                        backgroundColor: `${calendarColor}15`,
                        border: `1px solid ${calendarColor}40`,
                        animation: 'slideInRight 0.3s ease-out',
                      }}
                    >
                      <div className="flex-1">
                        <p className="font-medium" style={{ color: colors.textPrimary }}>{event.name}</p>
                        <p className="text-sm flex items-center gap-2" style={{ color: colors.textMuted }}>
                          <Calendar size={12} style={{ color: calendarColor }} />
                          {new Date(event.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          {event.time && (
                            <>
                              <Clock size={12} />
                              {event.time}
                            </>
                          )}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveEvent(index)}
                        className="p-1.5 rounded-full transition-all "
                        style={{ backgroundColor: `${colors.textMuted}20` }}
                      >
                        <X size={14} style={{ color: colors.textMuted }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-center text-sm" style={{ color: colors.textMuted }}>
                You can always add more events later
              </p>
              
              {/* Inline Finish Button */}
              <div className="flex justify-center mt-6">
                <button
                  onClick={handleFinish}
                  disabled={saving}
                  className="px-8 py-3 rounded-full font-semibold transition-all duration-150 transform active:scale-95 flex items-center gap-2"
                  style={{ 
                    backgroundColor: saving ? colors.bgTertiary : colors.accent, 
                    color: saving ? colors.textMuted : colors.bg,
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? (
                    <>
                      <div 
                        className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin"
                        style={{ borderColor: colors.textMuted, borderTopColor: 'transparent' }}
                      />
                      Saving...
                    </>
                  ) : (
                    <>
                      Finish Setup
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        );

      case 'pricing':
        return (
          <div className="px-6">
            <div className="flex justify-center mb-4">
              <Crown size={28} strokeWidth={1.25} style={{ color: colors.textPrimary }} />
            </div>
            <h2 
              className="text-[30px] sm:text-[40px] leading-[1.05] mb-2 font-semibold tracking-[-0.035em] max-w-md mx-auto w-full"
              style={{ color: colors.textPrimary }}
            >
              Unlock your full potential
            </h2>
            <p className="mb-6 max-w-md mx-auto w-full" style={{ color: colors.textSecondary }}>
              Go Pro for unlimited everything
            </p>

            {/* Billing toggle */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className="px-4 py-2 rounded-md text-sm font-medium transition-all duration-150"
                style={{ 
                  backgroundColor: billingPeriod === 'monthly' ? colors.accent : 'transparent',
                  color: billingPeriod === 'monthly' ? colors.bg : colors.textMuted,
                  border: billingPeriod === 'monthly' ? 'none' : `1px solid ${colors.border}`
                }}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className="px-4 py-2 rounded-md text-sm font-medium transition-all duration-150 relative"
                style={{ 
                  backgroundColor: billingPeriod === 'yearly' ? colors.accent : 'transparent',
                  color: billingPeriod === 'yearly' ? colors.bg : colors.textMuted,
                  border: billingPeriod === 'yearly' ? 'none' : `1px solid ${colors.border}`
                }}
              >
                Yearly
                <span 
                  className="absolute -top-2 -right-6 px-1.5 py-0.5 text-[9px] font-bold rounded-full"
                  style={{ backgroundColor: colors.warning, color: colors.bg }}
                >
                  -{Math.round(savings / (monthlyPrice * 12) * 100)}%
                </span>
              </button>
            </div>

            {/* Compact pricing card */}
            <div 
              className="max-w-sm mx-auto p-5 rounded-lg transition-all duration-150"
              style={{ 
                backgroundColor: colors.bgSecondary,
                border: `1px solid ${colors.accent}`,
                boxShadow: `0 0 30px ${colors.accent}15`,
              }}
            >
              {/* Price */}
              <div className="text-center mb-4">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-light" style={{ color: colors.textPrimary }}>
                    ${billingPeriod === 'yearly' ? yearlyMonthly : monthlyPrice}
                  </span>
                  <span style={{ color: colors.textMuted }}>/mo</span>
                </div>
                {billingPeriod === 'yearly' && (
                  <p className="text-sm mt-1" style={{ color: colors.accent }}>
                    Billed ${yearlyPrice}/year • Save ${savings}
                  </p>
                )}
              </div>

              {/* Benefits list */}
              <div className="space-y-2.5 mb-5">
                {[
                  { icon: Zap, text: 'Unlimited habits, tasks, goals & events' },
                  { icon: Calendar, text: 'Google Calendar sync' },
                  { icon: Sparkles, text: 'Advanced analytics & insights' },
                ].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div 
                      className="w-6 h-6 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${colors.accent}20` }}
                    >
                      <benefit.icon size={14} style={{ color: colors.accent }} />
                    </div>
                    <span className="text-sm" style={{ color: colors.textPrimary }}>
                      {benefit.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* Upgrade button */}
              <button
                onClick={() => {
                  navigate(`/upgrade?billing=${billingPeriod}`);
                }}
                className="w-full py-3 rounded-md font-semibold transition-all duration-150 transform active:scale-[0.98] flex items-center justify-center gap-2"
                style={{ backgroundColor: colors.accent, color: colors.onAccent }}
              >
                <Crown size={18} />
                Upgrade to Pro
              </button>
            </div>

            {/* Skip option */}
            <button
              onClick={handleNext}
              className="w-full mt-4 py-3 text-sm font-medium transition-all hover:opacity-70"
              style={{ color: colors.textMuted }}
            >
              Maybe later - continue with Free
            </button>
          </div>
        );

      case 'success':
        return (
          <div className="flex flex-col items-center text-center px-6">
            <div 
              className="w-14 h-14 rounded-full flex items-center justify-center mb-6 animate-scale-in-bounce"
              style={{ backgroundColor: colors.accent }}
            >
              <Check size={28} strokeWidth={2.5} color={colors.onAccent} />
            </div>
            <h1 
              className="text-3xl mb-4 font-semibold tracking-[-0.035em]"
              style={{ color: colors.textPrimary }}
            >
              You're all set!
            </h1>
            <p 
              className="mb-2 text-lg"
              style={{ color: colors.textSecondary }}
            >
              {(selectedHabits.size > 0 || selectedTasks.size > 0 || events.length > 0) ? (
                <>
                  <span style={{ color: colors.accent, fontWeight: 600 }}>{selectedHabits.size}</span> habits,{' '}
                  <span style={{ color: colors.info, fontWeight: 600 }}>{selectedTasks.size}</span> tasks, and{' '}
                  <span style={{ color: colors.warning, fontWeight: 600 }}>{events.length}</span> events added
                </>
              ) : (
                'Your dashboard is ready'
              )}
            </p>
            <p 
              className="mb-8 max-w-sm"
              style={{ color: colors.textMuted }}
            >
              Your journey to a better you starts now. One small step at a time.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-8 py-4 rounded-md font-semibold text-lg transition-all duration-150 transform active:scale-95 flex items-center gap-2"
              style={{ backgroundColor: colors.accent, color: colors.onAccent }}
            >
              Go to Dashboard
              <ArrowRight size={20} />
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 'motivation':
        return motivation !== null;
      case 'focus':
        return focusAreas.size > 0;
      case 'time':
        return timePreference !== null;
      default:
        return true;
    }
  };

  const showNavigation = !['welcome', 'success', 'pricing'].includes(currentStep);

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ 
        backgroundColor: colors.bg,
        fontFamily: fonts.sans 
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Header with close button and progress dots */}
      {currentStep !== 'success' && (
        <div className="px-6 pt-3">
          <div className="max-w-md mx-auto flex items-end justify-between gap-4 pb-3" style={{ borderBottom: `1px solid ${colors.textPrimary}` }}>
            <span className="text-[22px] leading-none" style={{ fontFamily: fonts.serif, color: colors.textPrimary }}>mind.</span>
            {showNavigation && (
              <span className="font-mono text-xs" style={{ color: colors.textSecondary }}>
                Step {step} of {totalSteps - 1}
              </span>
            )}
          </div>
          {showNavigation && (
            <div className="max-w-md mx-auto" style={{ height: 2, backgroundColor: colors.border }}>
              <div className="h-full transition-[width] duration-300" style={{ width: `${(step / (totalSteps - 1)) * 100}%`, backgroundColor: colors.textPrimary }} />
            </div>
          )}
        </div>
      )}

      {/* Main content area with slide animation */}
      <div 
        className="flex-1 flex flex-col pt-4 pb-8 overflow-y-auto"
        style={{
          opacity: isAnimating ? 0 : 1,
          transform: isAnimating 
            ? `translateX(${slideDirection === 'right' ? '-30px' : '30px'})` 
            : 'translateX(0)',
          transition: 'opacity 0.2s ease-out, transform 0.2s ease-out',
        }}
      >
        {renderStep()}
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes slideInRight {
          from { 
            opacity: 0; 
            transform: translateX(30px); 
          }
          to { 
            opacity: 1; 
            transform: translateX(0); 
          }
        }
        
        @keyframes slideInUp {
          from { 
            opacity: 0; 
            transform: translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        @keyframes bounceIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
        
        @keyframes swipeHint {
          0%, 100% { transform: translateX(0); opacity: 0.5; }
          50% { transform: translateX(8px); opacity: 1; }
        }
        
        @keyframes swipeFingerLeft {
          0%, 100% { transform: translateX(0); opacity: 0.6; }
          50% { transform: translateX(-4px); opacity: 1; }
        }
        
        @keyframes fadeInUp {
          from { 
            opacity: 0; 
            transform: translateY(10px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        @keyframes ripple {
          0% { 
            transform: scale(0);
            opacity: 1;
          }
          100% { 
            transform: scale(4);
            opacity: 0;
          }
        }
        
        .animate-ripple {
          animation: ripple 0.3s ease-out forwards;
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
};

export default OnboardingPage;
